import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Button,
  TextField,
  CircularProgress,
  Typography,
  Box,
  makeStyles,
} from "@material-ui/core";
import { Close } from "@material-ui/icons";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";

// Froala Editor
import "froala-editor/js/froala_editor.pkgd.min.js";
import "froala-editor/js/plugins.pkgd.min.js";
import "froala-editor/css/froala_style.min.css";
import "froala-editor/css/froala_editor.pkgd.min.css";
import FroalaEditor from "react-froala-wysiwyg";

// Your Redux actions
import { emailTemplateActions } from "../../../../../../store/emailTemplate/emailTemplateActions";
import { launchesActions } from "../../../../../../store/launches/launchesActions";

const useStyles = makeStyles(theme => ({
  dialogPaper: {
    borderRadius: "6px",
    maxWidth: "800px",
    width: "100%",
    maxHeight: "85vh",
    margin: "32px auto",
  },
  header: {
    fontSize: "22px",
    color: "#fff",
    padding: "15px 20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: "#3699FF",
    margin: 0,
    position: "relative",
    minHeight: "70px",
    fontWeight: 500,
    lineHeight: 1.2,
  },
  headerTitle: {
    fontSize: "22px",
    color: "#fff",
    fontWeight: 600,
    margin: 0,
    flex: 1,
    paddingRight: "40px",
  },
  closeButton: {
    position: "absolute",
    top: "50%",
    right: "15px",
    transform: "translateY(-50%)",
    color: "white",
    padding: "8px",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.1)",
    },
  },
  dialogContent: {
    padding: "20px !important",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  formContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  formField: {
    "& .MuiOutlinedInput-root": {
      height: "40px",
    },
    "& .MuiOutlinedInput-input": {
      padding: "10px 14px",
    },
  },
  editorContainer: {
    minHeight: "300px",
    marginTop: "10px",
    border: "1px solid #ddd",
    borderRadius: "4px",
    overflow: "hidden",
    "& .fr-wrapper": {
      minHeight: "200px",
    },
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
    marginTop: "20px",
    paddingTop: "20px",
    borderTop: "1px solid #e0e0e0",
  },
  errorText: {
    color: "#f44336",
    fontSize: "12px",
    marginTop: "4px",
  },
}));

new FroalaEditor(".selector", {
  toolbarButtons: {
    moreText: {
      buttons: [
        "bold",
        "italic",
        "underline",
        "strikeThrough",
        "subscript",
        "superscript",
        "fontFamily",
        "fontSize",
        "textColor",
        "backgroundColor",
        "inlineClass",
        "inlineStyle",
        "clearFormatting",
      ],
    },
    moreParagraph: {
      buttons: [
        "alignLeft",
        "alignCenter",
        "formatOLSimple",
        "alignRight",
        "alignJustify",
        "formatOL",
        "formatUL",
        "paragraphFormat",
        "paragraphStyle",
        "lineHeight",
        "outdent",
        "indent",
        "quote",
      ],
    },
    moreRich: {
      buttons: [
        "insertLink",
        "insertImage",
        // "insertVideo",
        "insertTable",
        "emoticons",
        "fontAwesome",
        "specialCharacters",
        "embedly",
        // "insertFile",
        // "insertHR",
      ],
    },
    moreMisc: {
      buttons: [
        "undo",
        "redo",
        "print",
        "getPDF",
        "spellChecker",
        "selectAll",
        "html",
        "help",
      ],
      align: "right",
      buttonsVisible: 2,
    },
  },
});

var test = "";

const AddEmailTemplateModal = ({
  open,
  onClose,
  onTemplateAdded,
  editTemplateData = null,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [formData, setFormData] = useState({
    id: "",
    title: "",
    subject: "",
    body: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imageUploadUrl, setImageUploadUrl] = useState("");

  // Reset form when modal opens/closes or edit data changes
  useEffect(() => {
    if (editTemplateData && open) {
      // Edit mode: populate form with template data
      setFormData({
        id: editTemplateData.id || "",
        title: editTemplateData.templateName || "",
        subject: editTemplateData.subject || "",
        body: editTemplateData.body || editTemplateData.content || "",
      });
    } else {
      // Add mode: reset form
      setFormData({
        id: "",
        title: "",
        subject: "",
        body: "",
      });
    }
    setErrors({});
    setIsSubmitting(false);
    setImageUploadUrl("");
  }, [editTemplateData, open]);

  const handleClose = () => {
    onClose();
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = "Title is required";
    }

    if (!formData.subject.trim()) {
      newErrors.subject = "Subject is required";
    }

    if (
      !formData.body.trim() ||
      formData.body === "<p><br></p>" ||
      formData.body === "<p></p>"
    ) {
      newErrors.body = "Email body is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleEditorChange = content => {
    setFormData(prev => ({
      ...prev,
      body: content,
    }));

    // Clear error when user starts typing
    if (errors.body) {
      setErrors(prev => ({
        ...prev,
        body: "",
      }));
    }
  };

  const uploadFroalaImage = file => {
    dispatch(launchesActions.uploadImage(file)).then(res => {
      if (res.success) {
        test = res.data.url;
      }
    });
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Replace placeholder image URLs if needed
      let processedBody = formData.body;
      if (imageUploadUrl) {
        processedBody = processedBody.replace(
          "https://via.placeholder.com/300x200?text=Uploading...",
          imageUploadUrl,
        );
      }

      const payload = {
        templateName: formData.title.trim(),
        subject: formData.subject.trim(),
        body: processedBody,
      };

      let response;

      if (editTemplateData) {
        // Update existing template
        payload.id = formData.id;
      }
      response = await dispatch(
        emailTemplateActions.onAddUpdateTemplate(payload),
      );
      if (response?.data?.success) {
        toast.success(
          editTemplateData
            ? "Email template updated successfully!"
            : "Email template created successfully!",
        );

        if (onTemplateAdded) {
          onTemplateAdded();
        }

        handleClose();
      } else {
        toast.error(response?.data?.message || "Failed to save template");
      }
    } catch (error) {
      console.error("Error saving email template:", error);
      toast.error("An error occurred while saving the template");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      classes={{
        paper: classes.dialogPaper,
      }}
      maxWidth='md'
      fullWidth
      disableBackdropClick={isSubmitting}
      disableEscapeKeyDown={isSubmitting}>
      <DialogTitle className={classes.header} disableTypography>
        <Typography variant='h2' className={classes.headerTitle}>
          {editTemplateData ? "Edit Email Template" : "Add New Email Template"}
        </Typography>
        <IconButton
          className={classes.closeButton}
          onClick={handleClose}
          size='small'
          disabled={isSubmitting}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        <div className={classes.formContainer}>
          <TextField
            label='Template Title *'
            name='title'
            value={formData.title}
            onChange={handleInputChange}
            fullWidth
            variant='outlined'
            size='small'
            className={classes.formField}
            disabled={isSubmitting}
            error={!!errors.title}
            helperText={errors.title}
          />

          <TextField
            label='Email Subject *'
            name='subject'
            value={formData.subject}
            onChange={handleInputChange}
            fullWidth
            variant='outlined'
            size='small'
            className={classes.formField}
            disabled={isSubmitting}
            error={!!errors.subject}
            helperText={errors.subject}
          />

          <Box>
            <Typography variant='subtitle2' gutterBottom>
              Email Body *
            </Typography>
            <div className={classes.editorContainer}>
              <FroalaEditor
                model={formData.body}
                onModelChange={handleEditorChange}
                tag='textarea'
                config={{
                  toolbarButtons: [
                    ["bold", "italic", "underline", "strikeThrough"],
                    ["fontFamily", "fontSize", "textColor", "backgroundColor"],
                    ["alignLeft", "alignCenter", "alignRight", "alignJustify"],
                    ["formatOL", "formatUL", "outdent", "indent"],
                    ["insertLink", "insertImage"], // Only link and image, no video or file
                    ["emoticons", "specialCharacters"],
                    ["clearFormatting"],
                  ],
                  videoInsert: false,
                  fileInsert: false,
                  events: {
                    "image.inserted": function ($img, response) {
                      $img[0].src =
                        "https://logosbynick.com/wp-content/uploads/2021/01/animated-gif.gif";
                      setTimeout(() => {
                        $img[0].src = test;
                      }, 7000);
                      console.log(this);
                    },
                    "image.beforeUpload": function (images) {
                      uploadFroalaImage(images);
                      console.log(this);
                    },
                  },
                  key: "VCC6kE4A3D3H3C7C7C6cWHNGGDTCWHIg1Ee1Oc2Yc1b1Lg1POkB6B5F5A3F3E3B3F2A5C3==",
                }}
              />
            </div>
            {errors.body && (
              <Typography className={classes.errorText}>
                {errors.body}
              </Typography>
            )}
          </Box>

          <div className={classes.actions}>
            <Button
              variant='outlined'
              style={{ textTransform: "none" }}
              onClick={handleClose}
              disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              variant='contained'
              color='primary'
              onClick={handleSubmit}
              disabled={isSubmitting}
              startIcon={
                isSubmitting ? (
                  <CircularProgress size={20} color='inherit' />
                ) : null
              }>
              {isSubmitting
                ? editTemplateData
                  ? "Updating..."
                  : "Creating..."
                : editTemplateData
                  ? "Update Template"
                  : "Create Template"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddEmailTemplateModal;
